import {LitElement, html, css} from 'lit'
import type {PropertyValues} from 'lit'
import {customElement, property} from 'lit/decorators.js'

/**
 * A simple error modal that displays a title message.
 * Based on the stop-practicing-modal from renaissance-global-header,
 * but without action buttons - purely informational.
 *
 * @example
 * ```html
 * <error-modal open title="Oops! Something went wrong"></error-modal>
 * ```
 */
@customElement('error-modal')
export class ErrorModal extends LitElement {
  static styles = css`
    :host {
      --error-modal-text-color: #202020;
      --error-modal-white: #ffffff;
      --error-modal-overlay: rgba(0, 0, 0, 0.4);
      --error-modal-icon-bg: #e8e4f3;
      --error-modal-icon-color: #8b7fc7;

      font-family:
        'Roboto',
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif;
      font-size: 14px;
      line-height: 1.5;
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    .modal-overlay {
      position: fixed;
      z-index: 99999999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      overflow: auto;
      background-color: var(--error-modal-overlay);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 15vh;
    }

    .modal-box {
      display: flex;
      flex-direction: column;
      background-color: var(--error-modal-white);
      max-width: 340px;
      width: 90%;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
      position: relative;
      padding: 32px 24px 24px;
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .icon-container {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background-color: var(--error-modal-icon-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
    }

    .question-icon {
      width: 48px;
      height: 48px;
      color: var(--error-modal-icon-color);
    }

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--error-modal-text-color);
      margin: 0;
      line-height: 1.4;
    }

    .modal-title:focus {
      outline: none;
    }

    :host([hidden]) {
      display: none;
    }
  `

  @property({type: Boolean, reflect: true})
  open: boolean = false

  @property({type: String})
  title: string = 'An error occurred'

  @property({type: Boolean})
  hideChrome: boolean = false

  willUpdate(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has('hideChrome')) {
      this.style.setProperty(
        '--error-modal-overlay',
        this.hideChrome ? '#ffffff' : 'rgba(0, 0, 0, 0.4)',
      )
    }
  }

  updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('open') && this.open) {
      this.updateComplete.then(() => {
        const heading = this.shadowRoot?.querySelector('#modal-title') as HTMLElement
        heading.focus()
      })
    }
  }

  /**
   * Show the modal
   */
  public show(): void {
    this.open = true
  }

  /**
   * Hide the modal
   */
  public hide(): void {
    if (!this.hideChrome) this.open = false
  }

  render() {
    if (!this.open) {
      return null
    }

    return html`
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div class="modal-box">
          <div class="modal-content">
            <div class="icon-container" aria-hidden="true">
              <svg class="question-icon" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 4C9.79 4 8 5.79 8 8h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"
                />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </div>

            <h2 id="modal-title" class="modal-title" tabindex="-1">${this.title}</h2>
          </div>
        </div>
      </div>
    `
  }
}
